/**
 * Push, in the packaged app (client-app/ANDROID.md §5): the shell's `ArkPush`
 * plugin (client-app/capacitor/android, ArkPushPlugin.java) hands the page
 * the phone's FCM token, which the page registers with the host over its own
 * session (`POST /api/notifications/subscriptions`), and names the bot a
 * tapped notification was about. In a browser there is no plugin and none of
 * this runs; the browser's own notifications (notifications.ts) do that job.
 *
 * The page does not bundle `@capacitor/core`. What the shell injects into the
 * page (JSExport.java) is `window.Capacitor` with `isNativePlatform`,
 * `isPluginAvailable`, and a `Plugins` map holding, for each native plugin, a
 * proxy whose methods call native and return promises. That proxy is used
 * directly; there is no `registerPlugin` to call.
 */

export interface PushRegistration {
  /** Absent when the build has no Firebase project, and push is simply off. */
  readonly token?: string;
  readonly device: string;
  readonly reason?: string;
}

/** What the plugin offers, as the shell's generated proxy exposes it. */
export interface ShellPush {
  register(): Promise<PushRegistration>;
  pendingAgent(): Promise<{ readonly agentId?: string }>;
  setVisibleAgent(options: { readonly agentId: string | null }): Promise<void>;
  addListener(event: "openAgent", listener: (event: { readonly agentId: string }) => void): Promise<{ remove(): Promise<void> }> | { remove(): Promise<void> };
}

/** The bridge the shell injects: whether this is the shell, and the plugins it was built with. */
export interface PushWindow {
  readonly Capacitor?: {
    readonly isNativePlatform?: () => boolean;
    readonly isPluginAvailable?: (name: string) => boolean;
    readonly Plugins?: Readonly<Record<string, ShellPush | undefined>>;
  };
}

/** Where the page runs: a browser, a shell built without the push plugin, or a shell with it. */
export type ShellPushState = "browser" | "no-plugin" | "ready";

const PLUGIN = "ArkPush";
let cached: ShellPush | null | undefined;

export function shellPushState(win: PushWindow): ShellPushState {
  const bridge = win.Capacitor;
  if (bridge?.isNativePlatform?.() !== true) return "browser";
  return bridge.isPluginAvailable?.(PLUGIN) === true && bridge.Plugins?.[PLUGIN] != null ? "ready" : "no-plugin";
}

/** The plugin's proxy, or null outside the shell or in a shell built without it. Found once. */
export function shellPush(win: PushWindow): ShellPush | null {
  if (cached !== undefined) return cached;
  cached = shellPushState(win) === "ready" ? (win.Capacitor!.Plugins![PLUGIN] as ShellPush) : null;
  return cached;
}

/** For tests: forget the plugin found, so another window can be tried. */
export function resetShellPush(): void {
  cached = undefined;
}

const REGISTERED_KEY = "ark-bot.push.registered";

/**
 * Whether the host has this phone's token for the first time, as far as this
 * page remembers, so the page can say so once; the same token at the next
 * start is a silent refresh. With no storage every start counts as the first.
 */
export function firstRegistration(storage: Pick<Storage, "getItem" | "setItem"> | null, token: string): boolean {
  if (storage == null) return true;
  try {
    if (storage.getItem(REGISTERED_KEY) === token) return false;
    storage.setItem(REGISTERED_KEY, token);
    return true;
  } catch {
    return true;
  }
}
